"use client";

import React, { useCallback } from "react";
import styled from "styled-components";
import Flex from "../Flex";
import Button from "../Button";
import Text from "../Text";
import { getColor } from "../utils";

const TableHeadWrapper = styled(Flex).attrs<{ top?: string }>(({ top }) => {
  return {
    height: "44px",
    borderBottom: "default",
    alignItems: "center",
    zIndex: "60",
    background: "grayLightest",
    position: "sticky",
    top: top || 0,
  };
})<{ $fixedWidths?: boolean }>`
  gap: ${p => (p.$fixedWidths ? "0" : "16px")};
  justify-content: ${p => (p.$fixedWidths ? "flex-start" : "space-between")};
`;

const Cell = styled(Flex).attrs({
  display: "inline-flex",
  overflowY: "hidden",
  overflowX: "hidden",
})``;

const ResizeHandle = styled.button.attrs({ type: "button", "aria-label": "Resize column" })`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 10px;
  cursor: col-resize;
  z-index: 10;
  box-sizing: border-box;
  padding: 0;
  touch-action: none;
  user-select: none;
  background: transparent;
  border: none;
  appearance: none;
  border-right: 1px solid ${getColor("grayLight")};
`;

const headerButtonProps = {
  buttonStyle: "tertiary" as "tertiary" | "primary" | "secondary" | "outline",
  size: "small" as "small" | "regular",
  fontStyle: "ui.smallBold",
  themeOverrides: {
    primaryColor: "grayDarker",
    primaryColorDark: "grayDarkest",
  },
};

const headerTextProps = {
  font: "ui.smallBold",
  color: "grayDarker",
};

const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

export interface TableColumn {
  name?: string;
  flex?: number | string;
  minWidth: number;
  flexGrow?: number;
  flexShrink?: number;
  sortKey?: string;
  justify?: string;
  renderer?: (data) => React.ReactNode;
  leftPadding?: string;
  resizable?: boolean;
}

type Props = {
  columns: Array<TableColumn>;
  sort?: string;
  onSort?: (sortKey: string) => void;
  checkbox?: boolean;
  headerSpacer: number;
  /** When provided, renders columns at fixed pixel widths instead of flex — required for resize support. */
  widths?: number[];
  /** Callback factory for pointer-down drag handles. Only rendered on columns with `resizable: true`. */
  getResizeHandleProps?: (colIndex: number) => {
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  };
  onSelectColumn?: (colIndex: number) => void;
  /** Override the sticky top offset */
  top?: string;
};

const TableHeader: React.FC<Props> = ({
  columns,
  sort,
  onSort,
  headerSpacer,
  widths,
  getResizeHandleProps,
  onSelectColumn,
  top,
}) => {
  const hasFixedWidths = Boolean(widths?.length);
  const totalWidth = columns.reduce((acc, column) => acc + column.minWidth, 0);

  const showResizeHandle = useCallback(
    (column: TableColumn) => hasFixedWidths && column.resizable && getResizeHandleProps,
    [hasFixedWidths, getResizeHandleProps]
  );

  const handleSortClick = (value: string) => {
    const baseKey = value.replace(/-/g, "");
    if (sort?.includes(baseKey)) {
      onSort(sort?.includes("-") ? baseKey : `-${baseKey}`);
    } else {
      onSort(`-${baseKey}`);
    }
  };

  const getSortIcon = (type: string) => {
    if (sort?.includes(type)) {
      return sort?.includes("-") ? "action.table-sort-descending" : "action.table-sort-ascending";
    }
    return "action.expand";
  };

  return (
    <TableHeadWrapper
      minWidth={`${totalWidth + headerSpacer}px`}
      $fixedWidths={hasFixedWidths}
      top={top}>
      {headerSpacer > 0 && <Flex width={`${headerSpacer}px`} />}
      {columns.map((column, idx) => {
        const cellProps = hasFixedWidths
          ? {
              px: "16px",
              position: "relative" as const,
              style: { width: widths[idx], flexShrink: 0, flexGrow: 0 },
            }
          : {
              pl: column.leftPadding,
              flex: `${column.flexGrow || 1} ${column.flexShrink || 1} ${column.minWidth}px`,
            };

        return (
          <Cell
            key={idx}
            justifyContent={column.justify || undefined}
            {...cellProps}
            role={onSelectColumn ? "button" : undefined}
            tabIndex={onSelectColumn ? 0 : undefined}
            onClick={onSelectColumn ? () => onSelectColumn(idx) : undefined}
            onKeyDown={
              onSelectColumn
                ? event => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelectColumn(idx);
                    }
                  }
                : undefined
            }>
            {column.sortKey ? (
              // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
              <div onClick={stopPropagation} style={{ display: "inline-flex" }}>
                <Button
                  text={column.name}
                  onClick={() => handleSortClick(column.sortKey)}
                  icon={getSortIcon(column.sortKey)}
                  {...headerButtonProps}
                />
              </div>
            ) : (
              <Text {...headerTextProps}>{column.name}</Text>
            )}
            {showResizeHandle(column) && (
              <ResizeHandle {...getResizeHandleProps(idx)} onClick={stopPropagation} />
            )}
          </Cell>
        );
      })}
    </TableHeadWrapper>
  );
};

export default TableHeader;
